import { useState, useCallback, useMemo } from 'react';
import { useGetTransportCloudEntries } from 'api/transportCloud';
import { debounce } from 'lodash';
import { GroupedItems } from '../TransportCloudTable.types';
import { attachContainerStatus, enrichItem, groupItemsByDate } from '../utils/tableUtils';

const DEBOUNCE_DELAY = 500;

export const useTableData = (params: any) => {
  const [groupedRows, setGroupedRows] = useState<GroupedItems[]>([]);
  const [paginationCount, setPaginationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { getTransportCloudEntries } = useGetTransportCloudEntries();

  const populateRows = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getTransportCloudEntries(params);
      const { data = [], count = 0 } = response?.data?.result || {};

      setPaginationCount(count);
      setGroupedRows(
        groupItemsByDate(data.map(attachContainerStatus).map(enrichItem)),
      );
    } catch (err: any) {
      if (!['CanceledError', 'ERR_CANCELED'].includes(err.name || err.code)) {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [getTransportCloudEntries, params]);

  const debouncedPopulateRows = useMemo(
    () => debounce(populateRows, DEBOUNCE_DELAY),
    [populateRows],
  );

  return {
    groupedRows,
    setGroupedRows,
    paginationCount,
    loading,
    error,
    populateRows,
    debouncedPopulateRows,
  };
};
